import { LinkField, LinkFieldValue } from '@sitecore-jss/sitecore-jss-react';
import {
  LinkDataLayerObject,
  isEmailLink,
  isExternalLink as checkIsExternalLink,
  findParentCategory,
  getComponentType,
} from 'lib/util/gtm-datalayer';
import { BoolOrStringType } from 'lib/util/strings';

/**
 * Converts and clones incoming field, extracting text along the way
 */
export const makeLinkFieldClone = ({
  field,
  suppressLinkText,
}: {
  field?: LinkField | LinkFieldValue;
  suppressLinkText?: BoolOrStringType;
}): {
  asLinkField: LinkField;
  cloneLinkField: LinkField;
  text?: string;
} => {
  const asLinkField: LinkField = !field?.value ? { value: { ...field } } : (field as LinkField);
  // Hack used to create a deep clone of link field.  Note when we upgrade to a more modern version of NodeJS, structuredClone should be used instead
  const cloneLinkField = JSON.parse(JSON.stringify(asLinkField));
  const text =
    suppressLinkText || !cloneLinkField?.value?.text
      ? cloneLinkField?.value?.href
      : cloneLinkField?.value?.text;
  return {
    asLinkField,
    cloneLinkField,
    text,
  };
};

/**
 * Builds a LinkDataLayerObject from a link and click event
 */
export const getLinkDataLayerDetails = ({
  isExternalLink,
  link,
  hrefWithoutQuery,
  e,
}: {
  isExternalLink?: boolean;
  link: LinkField;
  hrefWithoutQuery?: string;
  e: React.MouseEvent<HTMLAnchorElement>;
}): LinkDataLayerObject | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  let linkDataLayerObject: LinkDataLayerObject;
  // Note was iniitally doing contentCategory
  const contentCategory = findParentCategory(e.currentTarget) ?? 'N/A';
  const componentType = getComponentType(e.currentTarget) ?? 'N/A';
  if (link.value?.linktype === 'file' || link.value?.linktype === 'media') {
    const [fileName] = hrefWithoutQuery?.split('/')?.pop()?.split('.') ?? '';
    linkDataLayerObject = {
      event: 'e_fileDownload',
      fileName: fileName,
      linkUrl: link.value?.href,
      clickText: link.value?.text ?? 'N/A',
      contentCategory,
      componentType,
    };
  } else if (isEmailLink(link.value?.href)) {
    linkDataLayerObject = {
      event: 'e_email',
      clickText: (link.value?.text ?? '').replace('@', '_at_'),
      contentCategory,
      componentType,
    };
  } else {
    const isThisAnExternalLink = isExternalLink ?? checkIsExternalLink(e.currentTarget);
    linkDataLayerObject = {
      event: isThisAnExternalLink ? 'e_outboundClick' : 'e_inPageClick',
      clickText: link.value?.text ?? 'N/A',
      linkUrl: link.value?.href,
      contentCategory,
      componentType,
    };
  }
  return linkDataLayerObject;
};

/**
 * Determines whether a link should be suppressed (not rendered) based on the provided options.
 *
 * @param params - The options for evaluating link suppression.
 * @param params.isExperienceEditor - Indicates if the current context is the Experience Editor. If true, the link will not be suppressed.
 * @param params.suppressLinkText - Optional. If true, suppresses the link regardless of its text or href. Defaults to false.
 * @param params.link - The link field object to evaluate. Suppression occurs if both text and href are missing.
 * @returns True if the link should be suppressed (not rendered); otherwise, false.
 *
 * @example
 * // Example with suppressLinkText property
 * const shouldSuppress = shouldSuppressLink({
 *   isExperienceEditor: false,
 *   suppressLinkText: true,
 *   link: { value: { text: '', href: 'https://example.com' } },
 * });
 * // shouldSuppress === false (link is not suppressed because href exists, but suppressLinkText is true)
 *
 * @example
 * // Example without suppressLinkText property (defaults to false)
 * const shouldSuppress = shouldSuppressLink({
 *   isExperienceEditor: false,
 *   link: { value: { text: '', href: '' } },
 * });
 * // shouldSuppress === true (link is suppressed because both text and href are missing)
 */
export const shouldSuppressLink = ({
  isExperienceEditor,
  suppressLinkText = false,
  link,
}: {
  /** Indicates if the current context is the Experience Editor. */
  isExperienceEditor: boolean;
  /** Optional. If true, suppresses the link regardless of its text or href. Defaults to false. */
  suppressLinkText?: boolean;
  /** The link field object to evaluate. */
  link: LinkField;
}): boolean => !isExperienceEditor && !suppressLinkText && !link?.value?.text && !link?.value?.href;
